import { FC, useCallback, useEffect, useMemo } from 'react';
import {
	Alert,
	AlertTitle,
	Autocomplete,
	createFilterOptions,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	Link,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { PATHS } from 'routes/constants';
import {
	activatePpwrServiceRequest,
	clearPpwrJotformDetails,
	getPpwrFeeOptionsRequest,
	getPpwrJotformRequest,
	resetActivatePpwrService,
} from 'redux/reducers/ppwr/reducer';
import {
	getPpwrActivateServiceLoading,
	getPpwrActivateServiceSuccess,
	getPpwrActivateServiceWarning,
	getPpwrFeeOptions,
	getPpwrFeeOptionsLoading,
	getPpwrJotformDetails,
	getPpwrJotformDetailsLoading,
} from 'redux/reducers/ppwr/selectors';
import { TPpwrFee } from 'redux/reducers/ppwr/types';
import { getEmEnumsRequest } from 'redux/reducers/emManagement/reducer';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import {
	getEnumsInvoiceFeeTypeId,
	getEnumsLegalStatusEu,
	getEnumsPaymentMode,
} from 'redux/reducers/emManagement/selectors';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import { getEMerchant, getEMerchantLoader } from 'redux/reducers/monthlyFee/selectors';
import { TEMerchantItem } from 'redux/reducers/monthlyFee/types';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { getCountryListRequest } from 'redux/reducers/country/reducer';
import { getValidationSchema } from './validationSchema';
import {
	EActivateServiceMode,
	TActivateServiceDialogProps,
	TActivateServiceFormValues,
} from './types';
import { EasTableValue } from '../../../ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from '../../../ui/EasTable/types';

const EMPTY_VALUES: TActivateServiceFormValues = {
	fullName: '',
	tinNumber: '',
	contactName: '',
	nominatedEmail: '',
	phone: '',
	registeredCountryId: '',
	registeredPostalCode: '',
	regAddressCity: '',
	regStreetNo: '',
	officialSiteUrl: '',
	legalStatus: '',
	paymentHandlingMode: '',
	invoiceFeeTypeId: '',
	subscriptionPlanId: '',
	existingEmId: '',
};

const SUBMIT_LABELS: Record<EActivateServiceMode, string> = {
	[EActivateServiceMode.EXISTING]: 'Activate EPR/PPWR',
	[EActivateServiceMode.ASSIGN]: 'Activate EPR',
	[EActivateServiceMode.CREATE]: 'Create EM and Activate EPR/PPWR',
};

/** Same matching as the "Search EM" filter: any part of the full name, ignoring case. */
const emFilterOptions = createFilterOptions<TEMerchantItem>({
	ignoreCase: true,
	matchFrom: 'any',
	stringify: (em) => em.fullName,
});

const TEXT_FIELDS: { name: keyof TActivateServiceFormValues; label: string }[] = [
	{ name: 'fullName', label: 'EM Full Name' },
	{ name: 'tinNumber', label: 'TIN' },
	{ name: 'contactName', label: 'Contact Person' },
	{ name: 'nominatedEmail', label: 'Contact Email' },
	{ name: 'phone', label: 'Contact Phone' },
	{ name: 'registeredPostalCode', label: 'Address ZIP' },
	{ name: 'regAddressCity', label: 'Address City' },
	{ name: 'regStreetNo', label: 'Address Street' },
	{ name: 'officialSiteUrl', label: 'Official Site' },
];

const formatDateTime = (value: string | null | undefined) =>
	value ? DateTime.fromISO(value).toFormat('dd.MM.yyyy HH:mm') : '';

type TJotformAnswer = {
	key: string;
	name: string;
	question: string;
	answer: string;
	order: number;
};

/** Question texts may hold markup such as `<strong>`, only the text is shown. */
const stripHtml = (html: string) =>
	new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ?? '';

/** Answers break lines with `<br>`; the answer is rendered with `white-space: pre-line`. */
const brToNewLine = (text: string) => text.replace(/<br\s*\/?>/gi, '\n');

/** Turns a raw `answer` into text: lists are joined, compound fields (name, address) too. */
const formatAnswerValue = (value: unknown): string => {
	if (value === null || value === undefined) return '';
	if (Array.isArray(value)) return value.map(formatAnswerValue).filter(Boolean).join(', ');
	if (typeof value === 'object') {
		return Object.values(value as Record<string, unknown>)
			.map(formatAnswerValue)
			.filter(Boolean)
			.join(' ');
	}
	return brToNewLine(String(value)).trim();
};

/**
 * A Jotform answer looks like `{ name, text, order, answer, prettyFormat? }`.
 * `prettyFormat` is Jotform's own rendering of the answer, so it wins over `answer`.
 */
const parseJotformAnswer = (key: string, value: unknown): TJotformAnswer => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return { key, name: '', question: '', answer: formatAnswerValue(value), order: Infinity };
	}
	const item = value as Record<string, unknown>;
	const prettyFormat =
		typeof item.prettyFormat === 'string' ? brToNewLine(item.prettyFormat).trim() : '';
	const order = Number(item.order);
	return {
		key,
		name: typeof item.name === 'string' ? item.name : '',
		question: typeof item.text === 'string' ? stripHtml(item.text) : '',
		answer: prettyFormat || formatAnswerValue(item.answer),
		order: Number.isFinite(order) ? order : Infinity,
	};
};

const getFeeLabel = (fee: TPpwrFee) =>
	`${fee.planName ?? `Plan #${fee.id}`} (monthly ${fee.monthlyFee}, per order ${fee.perOrderFee})`;

const getEmLabel = (em: TEMerchantItem) => em.fullName ?? `EM #${em.id}`;

/** Rendered with `key={jotformId}`, so the form and the picked EM start empty for each submission. */
export const ActivateServiceDialog: FC<TActivateServiceDialogProps> = ({ jotformId, onClose }) => {
	const dispatch = useDispatch();
	const open = jotformId !== null;

	const jotform = useSelector(getPpwrJotformDetails);
	const jotformLoading = useSelector(getPpwrJotformDetailsLoading);
	const activateLoading = useSelector(getPpwrActivateServiceLoading);
	const activateSuccess = useSelector(getPpwrActivateServiceSuccess);
	const activateWarning = useSelector(getPpwrActivateServiceWarning);
	const feeOptions = useSelector(getPpwrFeeOptions);
	const feeOptionsLoading = useSelector(getPpwrFeeOptionsLoading);
	const emList = useSelector(getEMerchant);
	const emListLoading = useSelector(getEMerchantLoader);
	const enumsLegalStatusEu = useSelector(getEnumsLegalStatusEu);
	const enumsPaymentMode = useSelector(getEnumsPaymentMode);
	const enumsInvoiceFeeTypeId = useSelector(getEnumsInvoiceFeeTypeId);
	const countries = useSelector(getCountryListData);

	/** Details of a previously opened submission may still be in the store until the new ones load. */
	const currentJotform = jotform && jotform.id === jotformId ? jotform : null;

	const mode = useMemo(() => {
		if (currentJotform?.emId) return EActivateServiceMode.EXISTING;
		if (currentJotform?.emShouldExist) return EActivateServiceMode.ASSIGN;
		return EActivateServiceMode.CREATE;
	}, [currentJotform]);
	const readOnly = mode === EActivateServiceMode.EXISTING;

	useEffect(() => {
		if (jotformId !== null) dispatch(getPpwrJotformRequest({ jotformId }));
	}, [dispatch, jotformId]);

	useEffect(() => {
		if (!open) return;
		if (!feeOptions) dispatch(getPpwrFeeOptionsRequest());
		if (!emList) dispatch(getEMerchantRequest());
		if (!enumsLegalStatusEu) dispatch(getEmEnumsRequest({ enumName: EEmEnums.LEGAL_STATUS_EU }));
		if (!enumsPaymentMode) dispatch(getEmEnumsRequest({ enumName: EEmEnums.PAYMENT_MODE }));
		if (!enumsInvoiceFeeTypeId)
			dispatch(getEmEnumsRequest({ enumName: EEmEnums.INVOICE_FEE_TYPE_ID }));
		if (!countries) dispatch(getCountryListRequest());
	}, [
		dispatch,
		open,
		feeOptions,
		emList,
		enumsLegalStatusEu,
		enumsPaymentMode,
		enumsInvoiceFeeTypeId,
		countries,
	]);

	const formik = useFormik<TActivateServiceFormValues>({
		initialValues: EMPTY_VALUES,
		validationSchema: getValidationSchema(mode),
		onSubmit: (values) => {
			if (jotformId === null || !currentJotform) return;
			const country = countries?.find((item) => item.id === values.registeredCountryId);
			const emIdByMode: Record<EActivateServiceMode, number | null> = {
				[EActivateServiceMode.EXISTING]: currentJotform.emId,
				[EActivateServiceMode.ASSIGN]: values.existingEmId === '' ? null : values.existingEmId,
				// A new EM is created, so no EM must be sent.
				[EActivateServiceMode.CREATE]: null,
			};
			dispatch(
				activatePpwrServiceRequest({
					jotformId,
					body: {
						fullName: values.fullName,
						tinNumber: values.tinNumber,
						contactName: values.contactName,
						nominatedEmail: values.nominatedEmail,
						phone: values.phone,
						registeredCountryId: Number(values.registeredCountryId),
						registeredCountry: country?.name ?? currentJotform.registeredCountry ?? '',
						isEu: country?.isEu ?? currentJotform.isEu,
						registeredPostalCode: values.registeredPostalCode,
						regAddressCity: values.regAddressCity,
						regStreetNo: values.regStreetNo,
						officialSiteUrl: values.officialSiteUrl,
						legalStatus: values.legalStatus,
						paymentHandlingMode: values.paymentHandlingMode,
						invoiceFeeTypeId: Number(values.invoiceFeeTypeId),
						subscriptionPlanId: Number(values.subscriptionPlanId),
						isForced: !!activateWarning,
						emId: emIdByMode[mode],
					},
				}),
			);
		},
	});

	const { setValues } = formik;

	useEffect(() => {
		if (!currentJotform) return;
		setValues({
			fullName: currentJotform.fullName ?? '',
			tinNumber: currentJotform.tinNumber ?? '',
			contactName: currentJotform.contactName ?? '',
			nominatedEmail: currentJotform.nominatedEmail ?? '',
			phone: currentJotform.phone ?? '',
			registeredCountryId: currentJotform.registeredCountryId ?? '',
			registeredPostalCode: currentJotform.registeredPostalCode ?? '',
			regAddressCity: currentJotform.regAddressCity ?? '',
			regStreetNo: currentJotform.regStreetNo ?? '',
			officialSiteUrl: currentJotform.officialSiteUrl ?? '',
			legalStatus: currentJotform.legalStatus ?? '',
			paymentHandlingMode: currentJotform.paymentHandlingMode ?? '',
			invoiceFeeTypeId: currentJotform.invoiceFeeTypeId ?? '',
			subscriptionPlanId: '', // The user has to pick the plan
			existingEmId: '',
		}).catch(() => {});
	}, [currentJotform, setValues]);

	const handleClose = useCallback(() => {
		dispatch(resetActivatePpwrService());
		dispatch(clearPpwrJotformDetails());
		onClose();
	}, [dispatch, onClose]);

	useEffect(() => {
		if (open && activateSuccess) handleClose();
	}, [open, activateSuccess, handleClose]);

	const selectedFee = useMemo(
		() =>
			feeOptions?.find((fee) => String(fee.id) === String(formik.values.subscriptionPlanId)) ??
			null,
		[feeOptions, formik.values.subscriptionPlanId],
	);

	const selectedEm = useMemo(
		() => emList?.find((em) => em.id === formik.values.existingEmId) ?? null,
		[emList, formik.values.existingEmId],
	);

	const submissionInfo = useMemo(() => {
		if (!currentJotform) return [];
		return [
			{ label: 'Submission ID', value: currentJotform.submissionId },
			{ label: 'Submitted at', value: formatDateTime(currentJotform.createdAt) },
			{ label: 'Company', value: currentJotform.company },
			{ label: 'Trading names', value: currentJotform.tradingNames },
			{ label: 'Company registration', value: currentJotform.companyRegistration },
			{ label: 'Customer type', value: currentJotform.customerType },
			{ label: 'Confirmation email', value: currentJotform.confirmEmail },
		].filter((item) => item.value !== null && item.value !== undefined && item.value !== '');
	}, [currentJotform]);

	const answers = useMemo(
		() =>
			Object.entries(currentJotform?.answers ?? {})
				.map(([key, value]) => parseJotformAnswer(key, value))
				.filter((item) => item.answer)
				.sort((a, b) => a.order - b.order),
		[currentJotform],
	);

	const emManagementUrl = `${window.location.origin}${PATHS.EM_MANAGEMENT_PAGE}`;
	const fieldError = (name: keyof TActivateServiceFormValues) =>
		formik.touched[name] && formik.errors[name] ? String(formik.errors[name]) : undefined;

	const renderSelect = (
		name: 'legalStatus' | 'paymentHandlingMode' | 'invoiceFeeTypeId' | 'registeredCountryId',
		label: string,
		options: { value: string | number; label: string }[],
	) => (
		<FormControl fullWidth size="small" error={!!fieldError(name)} disabled={readOnly}>
			<InputLabel>{label}</InputLabel>
			<Select name={name} value={formik.values[name]} label={label} onChange={formik.handleChange}>
				{options.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label}
					</MenuItem>
				))}
			</Select>
			<FormHelperText>{fieldError(name)}</FormHelperText>
		</FormControl>
	);

	const isBusy = activateLoading || jotformLoading || !currentJotform;

	return (
		<Dialog open={open} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
			<DialogTitle color="primary">Activate EPR/PPWR Service</DialogTitle>
			<form
				onSubmit={formik.handleSubmit}
				style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
			>
				<DialogContent
					dividers
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}
				>
					{isBusy && (
						<Box
							sx={{
								position: 'absolute',
								inset: 0,
								zIndex: 1000,
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								backgroundColor: 'rgba(255, 255, 255, 0.7)',
							}}
						>
							<CircularProgress size={40} />
						</Box>
					)}

					{mode === EActivateServiceMode.ASSIGN && (
						<Alert severity="warning" variant="standard">
							<AlertTitle>Existing EAS customer</AlertTitle>
							During registration, the user indicated that they are already an existing EAS
							customer. Prior to creating a new account, please search for an existing EM account
							using their email address, a partial company name, or a partial TIN. When found,
							please select EM account from the list in &quot;Existing EM Account&quot; field.{' '}
							{/* <Link
								href={emManagementUrl}
								target="_blank"
								rel="noopener noreferrer"
								color="inherit"
								fontWeight="bold"
							>
								{emManagementUrl}
							</Link> */}
						</Alert>
					)}

					{mode === EActivateServiceMode.EXISTING && (
						<Alert severity="warning" variant="standard">
							<AlertTitle>Existing EAS customer</AlertTitle>
							This submission is already linked to EM{' '}
							<EasTableValue
								type={ETableStructureType.EM}
								value={{ id: currentJotform?.emId, name: currentJotform?.fullName }}
								size="l"
							/>
							{/* <Link
								href={`${PATHS.EM_MANAGEMENT_PAGE}/${String(currentJotform?.emId)}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								#{currentJotform?.emId}
							</Link> */}
							<br />
							Only the subscription can be changed.
						</Alert>
					)}

					{activateWarning && (
						<Alert severity="warning" variant="standard">
							<AlertTitle>Warning</AlertTitle>
							{activateWarning.message}
							{activateWarning.data?.em_id && (
								<p>
									<Link
										href={`${PATHS.EM_MANAGEMENT_PAGE}/${String(activateWarning.data.em_id)}`}
										target="_blank"
										rel="noopener noreferrer"
										color="inherit"
									>
										<strong>EM #{activateWarning.data.em_id}</strong>
									</Link>
								</p>
							)}
							<Typography variant="body2">
								You can still create this account if you click{' '}
								<strong>&quot;Force Create Account&quot;</strong>.
							</Typography>
						</Alert>
					)}

					<Grid container spacing={3}>
						<Grid item xs={12} md={5}>
							<Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 2, height: '100%' }}>
								<Typography variant="h6" color="primary" gutterBottom>
									Information
								</Typography>
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
									{submissionInfo.map((item) => (
										<Typography key={item.label} variant="body2">
											<strong>{item.label}:</strong> {item.value}
										</Typography>
									))}
								</Box>
								{answers.length > 0 && (
									<>
										<Divider sx={{ my: 1.5 }} />
										<Typography variant="subtitle2" gutterBottom>
											Form answers:
										</Typography>
										<Box
											sx={{
												display: 'flex',
												flexDirection: 'column',
												gap: 1.5,
												maxHeight: 360,
												overflowY: 'auto',
												wordBreak: 'break-word',
											}}
										>
											{answers.map((item) => (
												<Box key={item.key}>
													<Typography variant="caption" color="text.secondary">
														{item.key} {item.name}
													</Typography>
													{item.question && (
														<Typography variant="body2" fontWeight="bold">
															{item.question}
														</Typography>
													)}
													<Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
														{item.answer}
													</Typography>
												</Box>
											))}
										</Box>
									</>
								)}
							</Box>
						</Grid>

						<Grid item xs={12} md={7}>
							<Grid container spacing={2}>
								{mode === EActivateServiceMode.ASSIGN && (
									<Grid item xs={12}>
										<Autocomplete
											size="small"
											fullWidth
											options={emList ?? []}
											value={selectedEm}
											loading={emListLoading}
											filterOptions={emFilterOptions}
											onChange={(_event, value) => {
												formik.setFieldValue('existingEmId', value?.id ?? '').catch(() => {});
											}}
											isOptionEqualToValue={(option, value) => option.id === value.id}
											getOptionLabel={getEmLabel}
											renderInput={(params) => (
												<TextField
													{...params}
													name="existingEmId"
													label="Existing EM Account"
													required
													onBlur={() => {
														formik.setFieldTouched('existingEmId', true).catch(() => {});
													}}
													error={!!fieldError('existingEmId')}
													helperText={fieldError('existingEmId')}
												/>
											)}
										/>
									</Grid>
								)}

								{TEXT_FIELDS.map((field) => (
									<Grid item xs={12} sm={6} key={field.name}>
										<TextField
											fullWidth
											size="small"
											label={field.label}
											name={field.name}
											value={formik.values[field.name]}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											disabled={readOnly}
											error={!!fieldError(field.name)}
											helperText={fieldError(field.name)}
										/>
									</Grid>
								))}

								<Grid item xs={12} sm={6}>
									{renderSelect(
										'registeredCountryId',
										'Address Country',
										(countries ?? []).map((country) => ({
											value: country.id,
											label: country.name,
										})),
									)}
								</Grid>
								<Grid item xs={12} sm={6}>
									{renderSelect(
										'legalStatus',
										'Legal Status',
										(enumsLegalStatusEu ?? []).map((item) => ({
											value: item.value,
											label: item.displayName,
										})),
									)}
								</Grid>
								<Grid item xs={12} sm={6}>
									{renderSelect(
										'paymentHandlingMode',
										'Payment Handling Mode',
										(enumsPaymentMode ?? []).map((item) => ({
											value: item.value,
											label: item.displayName,
										})),
									)}
								</Grid>
								<Grid item xs={12} sm={6}>
									{renderSelect(
										'invoiceFeeTypeId',
										'Invoicing Fee',
										(enumsInvoiceFeeTypeId ?? []).map((item) => ({
											value: item.value,
											label: item.displayName,
										})),
									)}
								</Grid>
								<Grid item xs={12} sm={6}>
									<Autocomplete
										size="small"
										fullWidth
										options={feeOptions ?? []}
										loading={feeOptionsLoading}
										value={selectedFee}
										onChange={(_event, value) => {
											formik.setFieldValue('subscriptionPlanId', value?.id ?? '').catch(() => {});
										}}
										isOptionEqualToValue={(option, value) => option.id === value.id}
										getOptionLabel={getFeeLabel}
										renderInput={(params) => (
											<TextField
												{...params}
												name="subscriptionPlanId"
												label="Subscription"
												required
												onBlur={() => {
													formik.setFieldTouched('subscriptionPlanId', true).catch(() => {});
												}}
												error={!!fieldError('subscriptionPlanId')}
												helperText={fieldError('subscriptionPlanId')}
											/>
										)}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={handleClose} color="inherit">
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						color={activateWarning ? 'warning' : 'primary'}
						disabled={
							isBusy || (mode === EActivateServiceMode.ASSIGN && formik.values.existingEmId === '')
						}
					>
						{activateWarning ? 'Force Create Account' : SUBMIT_LABELS[mode]}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
